import { html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { BaseElement } from "../utils/ui-components.js";

import "../components/header-component.js";
import "../components/footer-component.js";
import "../components/api-docs/api-docs-code-block.js";
import "../components/api-docs/api-docs-getting-started.js";
import "../components/api-docs/api-docs-auth.js";
import "../components/api-docs/api-docs-rate-limiting.js";
import "../components/api-docs/api-docs-keywords.js";
import "../components/api-docs/api-docs-errors.js";
import "../components/api-docs/api-docs-reference.js";

interface NavSection {
    id: string;
    label: string;
}

const SECTIONS: NavSection[] = [
    { id: "getting-started", label: "Getting Started" },
    { id: "authentication", label: "Authentication" },
    { id: "rate-limiting", label: "Rate Limiting" },
    { id: "keywords", label: "Keywords" },
    { id: "errors", label: "Errors" },
    { id: "reference", label: "Reference" },
];

@customElement("api-docs-page")
export class ApiDocsPage extends BaseElement {
    @state() private apiKey = "";
    @state() private activeSection = "getting-started";
    @state() private mobileNavOpen = false;

    private observer: IntersectionObserver | null = null;

    firstUpdated() {
        this.setupIntersectionObserver();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.observer?.disconnect();
    }

    private setupIntersectionObserver() {
        this.observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        this.activeSection = entry.target.id;
                        break;
                    }
                }
            },
            {
                rootMargin: "-20% 0px -60% 0px",
                threshold: 0,
            }
        );

        for (const section of SECTIONS) {
            const el = this.querySelector(`#${section.id}`);
            if (el) this.observer.observe(el);
        }
    }

    private get displayKey(): string {
        return this.apiKey.trim() || "YOUR_API_KEY";
    }

    private get baseUrl(): string {
        if (typeof location === "undefined") return "https://appshovel.com";
        const origin = location.origin;
        return origin.includes("localhost") || origin.includes("192.168.1") ? origin : "https://appshovel.com";
    }

    private handleApiKeyInput(e: InputEvent) {
        this.apiKey = (e.target as HTMLInputElement).value;
    }

    private scrollToSection(id: string) {
        const el = this.querySelector(`#${id}`);
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
        this.mobileNavOpen = false;
    }

    private renderNavList() {
        return html`
            <ul class="menu menu-sm w-full p-0">
                ${SECTIONS.map(
                    (section) => html`
                        <li>
                            <a
                                class="rounded-lg ${this.activeSection === section.id ? "active" : ""}"
                                @click=${(e: Event) => {
                                    e.preventDefault();
                                    this.scrollToSection(section.id);
                                }}
                                href="#${section.id}"
                            >
                                ${section.label}
                            </a>
                        </li>
                    `
                )}
            </ul>
        `;
    }

    private renderSidebar() {
        return html` <nav>${this.renderNavList()}</nav> `;
    }

    private renderApiKeyInput() {
        return html`
            <div class="flex flex-col sm:flex-row items-start sm:items-center gap-2 p-4 bg-base-200 rounded-lg">
                <label class="font-semibold text-sm whitespace-nowrap" for="api-key-input">API Key:</label>
                <input
                    id="api-key-input"
                    type="text"
                    placeholder="Paste your API key to populate examples"
                    class="input input-bordered input-sm w-full max-w-md font-mono"
                    .value=${this.apiKey}
                    @input=${this.handleApiKeyInput}
                />
                <span class="text-xs text-base-content/50">Your key stays in the browser — it's never sent anywhere.</span>
            </div>
        `;
    }

    private renderMobileNav() {
        return html`
            <div class="lg:hidden">
                <button class="btn btn-sm w-full justify-between" @click=${() => (this.mobileNavOpen = !this.mobileNavOpen)}>
                    <span>${SECTIONS.find((s) => s.id === this.activeSection)?.label ?? "Sections"}</span>
                    <svg
                        class="w-4 h-4 transition-transform ${this.mobileNavOpen ? "rotate-180" : ""}"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                    </svg>
                </button>
                ${this.mobileNavOpen ? html` <div class="mt-2 p-2 bg-base-200 rounded-lg">${this.renderNavList()}</div> ` : ""}
            </div>
        `;
    }

    render() {
        return html`
            <div class="flex flex-col min-h-screen container max-w-6xl p-4 md:p-6 lg:p-8">
                <header-component></header-component>

                <h1 class="text-3xl font-bold">API Documentation</h1>
                <p class="mt-2 text-base-content/60">
                    Prefer the command line? See the <a href="/docs/cli" class="link link-primary">CLI Documentation</a>.
                </p>

                <div class="flex flex-col gap-2 mt-4 lg:hidden">${this.renderMobileNav()} ${this.renderApiKeyInput()}</div>

                <div class="flex flex-col lg:flex-row gap-8 mt-6 flex-1">
                    <!-- Sidebar (desktop) -->
                    <aside class="hidden lg:block lg:w-56 shrink-0">
                        <div class="sticky top-8">${this.renderSidebar()}</div>
                    </aside>

                    <!-- Main content -->
                    <main class="flex-1 min-w-0 space-y-12">
                        <div class="hidden lg:block">${this.renderApiKeyInput()}</div>
                        <api-docs-getting-started id="getting-started" .apiKey=${this.displayKey} .baseUrl=${this.baseUrl}></api-docs-getting-started>
                        <div class="divider"></div>
                        <api-docs-auth id="authentication" .apiKey=${this.displayKey}></api-docs-auth>
                        <div class="divider"></div>
                        <api-docs-rate-limiting id="rate-limiting"></api-docs-rate-limiting>
                        <div class="divider"></div>
                        <api-docs-keywords id="keywords" .apiKey=${this.displayKey} .baseUrl=${this.baseUrl}></api-docs-keywords>
                        <div class="divider"></div>
                        <api-docs-errors id="errors"></api-docs-errors>
                        <div class="divider"></div>
                        <api-docs-reference id="reference"></api-docs-reference>
                    </main>
                </div>

                <div class="grow"></div>
                <footer-component></footer-component>
            </div>
        `;
    }
}
